import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "courseSelect",
    "calendarContainer",
    "calendar",
    "monthYear",
    "sessionInput",
    "dateInfoContainer",
    "selectedDate",
    "timeSlotStart",
    "timeSlotEnd",
    "emptyState",
  ];

  static values = {
    sessionsUrl: String,
    // { courseId: "YYYY-MM-DD" } — dernière séance déjà tenue de chaque cours
    latestSessions: Object,
    // Date de la séance déjà enregistrée (retour sur la page, erreur)
    selectedDate: String,
    emptyMonthText: String,
    noSessionText: String,
  };

  connect() {
    this.sessions = [];
    this.selectedDateStr = null;
    this.currentDate = this.initialMonth();
    this.initializeCalendar();
    // Load sessions if a course is already selected (e.g., after validation error)
    if (this.courseSelectTarget.value) {
      this.loadSessions();
    }
  }

  // Mois d'ouverture : celui de la séance déjà choisie, sinon celui de la
  // dernière séance tenue du cours — le mois courant n'a aucune séance quand
  // l'année scolaire affichée est terminée.
  initialMonth() {
    const dateStr =
      this.selectedDateValue ||
      this.latestSessionsValue[this.courseSelectTarget.value];
    return this.monthOf(dateStr) || new Date();
  }

  monthOf(dateStr) {
    const m = String(dateStr || "").match(/^(\d{4})-(\d{2})/);
    return m ? new Date(Number(m[1]), Number(m[2]) - 1, 1) : null;
  }

  todayStr() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate(),
    ).padStart(2, "0")}`;
  }

  // Changement de cours : la séance choisie appartenait à l'ancien cours.
  courseChanged() {
    this.sessionInputTarget.value = "";
    this.selectedDateStr = null;
    if (this.hasDateInfoContainerTarget) {
      this.dateInfoContainerTarget.classList.add("hidden");
    }
    const latest = this.monthOf(
      this.latestSessionsValue[this.courseSelectTarget.value],
    );
    if (latest) this.currentDate = latest;
    this.updateMonthYear();
    this.loadSessions();
  }

  // Message sous la grille quand le mois affiché n'a aucune séance cliquable.
  updateEmptyState(selectableCount) {
    if (!this.hasEmptyStateTarget) return;

    const courseId = this.courseSelectTarget.value;
    const show = Boolean(courseId) && this.loaded && selectableCount === 0;
    this.emptyStateTarget.classList.toggle("hidden", !show);
    if (!show) return;

    this.emptyStateTarget.textContent = this.latestSessionsValue[courseId]
      ? this.emptyMonthTextValue
      : this.noSessionTextValue;
  }

  initializeCalendar() {
    this.updateMonthYear();
    this.renderCalendar();
  }

  updateMonthYear() {
    const monthNames = [
      "Janvier",
      "Février",
      "Mars",
      "Avril",
      "Mai",
      "Juin",
      "Juillet",
      "Août",
      "Septembre",
      "Octobre",
      "Novembre",
      "Décembre",
    ];
    this.monthYearTarget.textContent = `${
      monthNames[this.currentDate.getMonth()]
    } ${this.currentDate.getFullYear()}`;
  }

  renderCalendar() {
    const year = this.currentDate.getFullYear();
    const month = this.currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    let firstDayOfWeek = firstDay.getDay();
    firstDayOfWeek = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;

    this.calendarTarget.innerHTML = "";

    // Get the selected session ID if any
    const selectedSessionId = this.sessionInputTarget.value;
    // Une séance à venir n'est pas sélectionnable : on n'est pas en retard
    // à un cours qui n'a pas eu lieu (le modèle la refuse aussi).
    const today = this.todayStr();
    let selectableCount = 0;

    // Empty days before first day of month
    for (let i = 0; i < firstDayOfWeek; i++) {
      const emptyDay = document.createElement("div");
      emptyDay.classList.add("p-1", "h-8", "text-center");
      this.calendarTarget.appendChild(emptyDay);
    }

    // Days of month
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const dayElement = document.createElement("div");
      const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(
        day,
      ).padStart(2, "0")}`;

      // Find sessions for this date
      const sessionsForDate =
        dateStr > today
          ? []
          : this.sessions.filter((s) => s.attributes.session_date === dateStr);
      if (sessionsForDate.length > 0) selectableCount++;

      // Check if this date contains the selected session (id match couvre le
      // retour sur la page après erreur ; selectedDateStr la sélection en cours)
      const isSelected =
        this.selectedDateStr === dateStr ||
        sessionsForDate.some(
          (s) => String(s.id) === String(selectedSessionId),
        );

      dayElement.textContent = day;
      dayElement.classList.add("p-1", "h-8", "text-center", "rounded");

      if (sessionsForDate.length > 0) {
        if (isSelected) {
          // Selected session - dark green
          dayElement.classList.add(
            "bg-cta",
            "text-white",
            "cursor-pointer",
            "font-medium",
          );
        } else {
          // Available session - light green
          dayElement.classList.add(
            "bg-cta-shared",
            "hover:bg-cta-shared-hover",
            // Its own text colour: in dark mode the inherited white was unreadable.
            "text-cta-darker",
            "cursor-pointer",
            "font-medium",
          );
        }
        dayElement.dataset.date = dateStr;
        dayElement.dataset.action = "click->lateness-calendar#selectDate";
      } else {
        dayElement.classList.add("text-gray-300");
      }

      // Highlight today
      if (
        day === new Date().getDate() &&
        month === new Date().getMonth() &&
        year === new Date().getFullYear()
      ) {
        dayElement.classList.add("border", "border-blue-500");
      }

      this.calendarTarget.appendChild(dayElement);
    }

    this.updateEmptyState(selectableCount);
  }

  previousMonth() {
    this.currentDate = new Date(
      this.currentDate.getFullYear(),
      this.currentDate.getMonth() - 1,
      1,
    );
    this.updateMonthYear();
    this.loadSessions();
  }

  nextMonth() {
    this.currentDate = new Date(
      this.currentDate.getFullYear(),
      this.currentDate.getMonth() + 1,
      1,
    );
    this.updateMonthYear();
    this.loadSessions();
  }

  loadSessions() {
    const courseId = this.courseSelectTarget.value;
    if (!courseId) {
      this.sessions = [];
      this.loaded = false;
      this.renderCalendar();
      return;
    }

    if (!this.hasSessionsUrlValue) {
      console.error("Sessions URL is not configured");
      return;
    }

    const year = this.currentDate.getFullYear();
    const month = String(this.currentDate.getMonth() + 1).padStart(2, "0");
    const start_date = `${year}-${month}-01`;
    const end_date = `${year}-${month}-${new Date(
      year,
      this.currentDate.getMonth() + 1,
      0,
    ).getDate()}`;

    let baseUrl = this.sessionsUrlValue
      .replace(":id", courseId)
      .replace("course_id=%3Aid", `course_id=${courseId}`);
    const separator = baseUrl.includes("?") ? "&" : "?";
    const url = `${baseUrl}${separator}start_date=${start_date}&end_date=${end_date}`;

    fetch(url)
      .then((response) => response.json())
      .then((data) => {
        this.sessions = data.data || [];
        this.loaded = true;
        this.renderCalendar();
        this.showSelectionFromInput();
      })
      .catch((error) => {
        console.error("Error loading sessions:", error);
        this.sessions = [];
        this.renderCalendar();
      });
  }

  // Après chargement, si l'input contient déjà une séance de ce mois
  // (retour sur la page, erreur de validation), affiche le panneau récap.
  showSelectionFromInput() {
    const selectedSessionId = this.sessionInputTarget.value;
    if (!selectedSessionId) return;

    const session = this.sessions.find(
      (s) => String(s.id) === String(selectedSessionId),
    );
    if (session) this.updateDateInfo(session);
  }

  selectDate(event) {
    const dateStr = event.currentTarget.dataset.date;
    if (!dateStr) return;

    // Find all sessions for this date
    const sessionsForDate = this.sessions.filter(
      (s) => s.attributes.session_date === dateStr,
    );

    if (sessionsForDate.length === 0) return;

    // For now, select the first session of the day
    // TODO: If multiple sessions, show a modal to choose
    const session = sessionsForDate[0];

    // Set the session ID in the hidden input
    this.sessionInputTarget.value = session.id;
    this.selectedDateStr = dateStr;

    // Affiche le récap de la séance sous le calendrier
    this.updateDateInfo(session);

    // Re-render pour appliquer la sélection (et rendre son vert au jour
    // précédemment sélectionné)
    this.renderCalendar();

    // Trigger change event so form knows a session was selected
    this.sessionInputTarget.dispatchEvent(new Event("change"));
  }

  // Met à jour le panneau « Séance sélectionnée » (date + horaires)
  updateDateInfo(session) {
    const dateStr = session.attributes.session_date;
    const timeOf = (iso) => {
      const m = String(iso || "").match(/(\d{2}:\d{2})/);
      return m ? m[1] : "";
    };

    if (this.hasSelectedDateTarget) {
      this.selectedDateTarget.textContent = new Date(dateStr).toLocaleDateString(
        "fr-FR",
        { weekday: "long", day: "numeric", month: "long", year: "numeric" },
      );
    }
    if (this.hasTimeSlotStartTarget) {
      this.timeSlotStartTarget.textContent = timeOf(session.attributes.start_time);
    }
    if (this.hasTimeSlotEndTarget) {
      this.timeSlotEndTarget.textContent = timeOf(session.attributes.end_time);
    }
    if (this.hasDateInfoContainerTarget) {
      this.dateInfoContainerTarget.classList.remove("hidden");
    }
  }
}
